import { useRef, useState, useCallback, useEffect } from "react";
import DOMPurify from "dompurify";
import ImageUploader from "./ImageUploader";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  folder?: string;
  label?: string;
}

interface ToolbarButtonProps {
  title: string;
  icon: string;
  active?: boolean;
  onClick: () => void;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ title, icon, active, onClick }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={`w-7 h-7 flex items-center justify-center rounded transition-colors cursor-pointer text-sm ${
      active ? "bg-[#004A99] text-white" : "text-gray-600 hover:bg-gray-100 hover:text-[#004A99]"
    }`}
  >
    <i className={icon} />
  </button>
);

const ToolbarDivider = () => (
  <div className="w-px h-5 bg-gray-200 mx-1" />
);

/**
 * Auto-detect bare URLs in TEXT nodes only (not inside HTML attributes).
 * Splits HTML into tag tokens and text tokens, only processes text tokens.
 * This prevents matching URLs inside href="...", target="...", rel="..." attributes.
 */
const autoLinkText = (html: string): string => {
  const parts = html.split(/(<[^>]+>)/g);
  return parts
    .map((part) => {
      if (part.startsWith("<") && part.endsWith(">")) return part;
      return part.replace(
        /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi,
        (url) => {
          const href = url.startsWith("http") ? url : `https://${url}`;
          return `<a href="${href}" target="_blank" rel="noopener noreferrer nofollow">${url}</a>`;
        }
      );
    })
    .join("");
};

/**
 * Sanitize HTML pasted from Google Docs using DOMPurify:
 * - Remove all inline style="" attributes (Google Docs adds tons of these)
 * - Remove class="" attributes from Google Docs
 * - Remove empty spans/divs left after stripping
 * - Convert <div> line breaks to <p> tags
 * - Keep semantic tags: b, i, u, strong, em, ul, ol, li, h1-h6, blockquote, a, img, br, p
 * - Supports UTF-8 / emoji natively (browser handles this)
 * - Auto-detect and linkify URLs
 */
const sanitizeGoogleDocsHtml = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const walk = (node: Element) => {
    // Remove style and class attributes (Google Docs inline styles)
    node.removeAttribute("style");
    node.removeAttribute("class");
    node.removeAttribute("id");
    node.removeAttribute("dir");
    node.removeAttribute("lang");
    node.removeAttribute("data-sheets-value");
    node.removeAttribute("data-sheets-formula");

    // Convert <div> to <p> for proper paragraph spacing
    if (node.tagName === "DIV") {
      const p = doc.createElement("p");
      while (node.firstChild) p.appendChild(node.firstChild);
      node.replaceWith(p);
      walk(p);
      return;
    }

    // Remove empty spans (Google Docs wraps everything in spans)
    if (node.tagName === "SPAN" && !node.hasChildNodes()) {
      node.remove();
      return;
    }

    // Unwrap spans that only contain text (no meaningful formatting)
    if (node.tagName === "SPAN") {
      const parent = node.parentNode;
      if (parent) {
        while (node.firstChild) parent.insertBefore(node.firstChild, node);
        node.remove();
        return;
      }
    }

    // Recursively process children (use Array.from to avoid live NodeList issues)
    Array.from(node.children).forEach((child) => walk(child as Element));
  };

  Array.from(doc.body.children).forEach((child) => walk(child as Element));

  // Clean up: remove consecutive empty paragraphs (Google Docs adds many)
  let result = doc.body.innerHTML;
  result = result.replace(/<p>\s*<\/p>/gi, "");
  result = result.replace(/(<br\s*\/?>\s*){3,}/gi, "<br><br>");
  // Ensure line breaks between block elements
  result = result.replace(/<\/p>\s*<p>/gi, "</p><p>");

  // Auto-link bare URLs
  result = autoLinkText(result);

  // Final DOMPurify sanitize - allow safe HTML tags but strip dangerous attributes
  result = DOMPurify.sanitize(result, {
    ALLOWED_TAGS: [
      "p", "br", "b", "strong", "i", "em", "u", "s", "strike",
      "h1", "h2", "h3", "h4", "h5", "h6",
      "ul", "ol", "li",
      "blockquote", "pre", "code",
      "a", "img",
      "span", "div",
      "table", "thead", "tbody", "tr", "th", "td",
      "hr",
    ],
    ALLOWED_ATTR: [
      "href", "src", "alt", "title", "target", "rel",
      "style", "class",
      "width", "height", "data-size",
      "colspan", "rowspan",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
  });

  return result;
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Nhập nội dung...",
  minHeight = 240,
  folder = "content",
  label,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [imageSize, setImageSize] = useState("50");
  const [imageAlign, setImageAlign] = useState<"left" | "center" | "right">("center");
  const savedRange = useRef<Range | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);
  const [selectedImageAlign, setSelectedImageAlign] = useState<"left" | "center" | "right">("center");
  const [resizing, setResizing] = useState(false);
  const [resizeDimensions, setResizeDimensions] = useState<{ w: number; h: number } | null>(null);

  // Sync value into editor when external value changes
  useEffect(() => {
    if (!editorRef.current || isInternalChange.current) return;
    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    isInternalChange.current = true;
    onChange(editorRef.current.innerHTML);
    requestAnimationFrame(() => { isInternalChange.current = false; });
  }, [onChange]);

  // Handle paste event - intercept and sanitize Google Docs content
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const clipboardData = e.clipboardData;
    const htmlData = clipboardData.getData("text/html");
    const textData = clipboardData.getData("text/plain");

    if (htmlData) {
      e.preventDefault();
      const sanitized = sanitizeGoogleDocsHtml(htmlData);
      document.execCommand("insertHTML", false, sanitized);
      handleInput();
    } else if (textData) {
      // Plain text paste: auto-link URLs and preserve line breaks
      e.preventDefault();
      // Convert newlines to <br> and auto-link
      const withBreaks = textData
        .split("\n")
        .map((line) => line.trim())
        .join("<br>");
      const linked = autoLinkText(withBreaks);
      const safe = DOMPurify.sanitize(linked, {
        ALLOWED_TAGS: ["br", "a", "b", "i", "u", "img"],
        ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "style", "width", "height"],
      });
      document.execCommand("insertHTML", false, safe);
      handleInput();
    }
  }, [handleInput]);

  const execCmd = useCallback((cmd: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    handleInput();
  }, [handleInput]);

  const isActive = (cmd: string) => {
    try { return document.queryCommandState(cmd); } catch { return false; }
  };

  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedRange.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreRange = () => {
    if (!savedRange.current) return;
    const sel = window.getSelection();
    if (sel) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
  };

  const handleFontSize = (size: string) => {
    execCmd("fontSize", "7");
    if (!editorRef.current) return;
    const fontEls = editorRef.current.querySelectorAll("font[size='7']");
    fontEls.forEach((el) => {
      const span = document.createElement("span");
      span.style.fontSize = size;
      while (el.firstChild) span.appendChild(el.firstChild);
      el.replaceWith(span);
    });
    handleInput();
  };

  const handleHeading = (tag: string) => {
    execCmd("formatBlock", tag);
  };

  const handleColor = (color: string) => {
    execCmd("foreColor", color);
  };

  const handleBgColor = (color: string) => {
    execCmd("hiliteColor", color);
  };

  const handleLink = () => {
    const url = window.prompt("Nhập URL liên kết:", "https://");
    if (url) {
      execCmd("createLink", url);
      // After creating link, set target="_blank" on the newly created link
      if (editorRef.current) {
        const links = editorRef.current.querySelectorAll(`a[href="${url}"]`);
        links.forEach((link) => {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer nofollow");
        });
        handleInput();
      }
    }
  };

  const handleInsertImages = () => {
    if (!imageUrls.length) return;
    restoreRange();
    editorRef.current?.focus();
    const alignStyleMap = {
      left: "display:block; float:left; margin:0 16px 8px 0;",
      center: "display:block; margin:8px auto;",
      right: "display:block; float:right; margin:0 0 8px 16px;",
    };
    imageUrls.forEach((url) => {
      // Use width instead of max-width so the size is preserved when rendered in any container
      // Also add data-size attribute as fallback
      const html = `<img src="${url}" style="${alignStyleMap[imageAlign]} width:${imageSize}%; border-radius:8px;" data-size="${imageSize}" alt="image" />`;
      document.execCommand("insertHTML", false, html);
    });
    handleInput();
    setShowImageModal(false);
    setImageUrls([]);
  };

  const COLORS = ["#000000","#1a1a1a","#444","#666","#999","#004A99","#00AEEF","#e03e3e","#e88c30","#2d9e2d","#9b59b6","#ffffff"];
  const BG_COLORS = ["#fff9c4","#c8e6c9","#bbdefb","#f8d7da","#e1d5e7","#fff","transparent"];

  // Image click to select + show resize handles
  const handleEditorClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "IMG") {
      const img = target as HTMLImageElement;
      setSelectedImageSrc(img.src);
      setSelectedImageAlign(
        img.style.margin === "8px auto" ? "center" :
        img.style.float === "right" ? "right" : "left"
      );
      if (overlayRef.current && wrapperRef.current) {
        const imgRect = img.getBoundingClientRect();
        const wrapRect = wrapperRef.current.getBoundingClientRect();
        const scrollTop = wrapperRef.current.scrollTop ?? 0;
        const scrollLeft = wrapperRef.current.scrollLeft ?? 0;
        overlayRef.current.style.top = `${imgRect.top - wrapRect.top + scrollTop}px`;
        overlayRef.current.style.left = `${imgRect.left - wrapRect.left + scrollLeft}px`;
        overlayRef.current.style.width = `${img.offsetWidth}px`;
        overlayRef.current.style.height = `${img.offsetHeight}px`;
      }
    } else {
      setSelectedImageSrc(null);
    }
  };

  const setSelectedImageAlignment = (align: "left" | "center" | "right") => {
    if (!selectedImageSrc || !editorRef.current) return;
    const img = editorRef.current.querySelector(`img[src="${selectedImageSrc}"]`) as HTMLImageElement | null;
    if (!img) return;
    if (align === "center") {
      img.style.display = "block";
      img.style.margin = "8px auto";
      img.style.float = "";
    } else if (align === "left") {
      img.style.display = "block";
      img.style.float = "left";
      img.style.margin = "0 16px 8px 0";
    } else {
      img.style.display = "block";
      img.style.float = "right";
      img.style.margin = "0 0 8px 16px";
    }
    setSelectedImageAlign(align);
    handleInput();
  };

  const startResize = (e: React.MouseEvent, corner: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!editorRef.current || !selectedImageSrc) return;
    const img = editorRef.current.querySelector(`img[src="${CSS.escape ? CSS.escape(selectedImageSrc) : selectedImageSrc.replace(/["\\]/g, "\\$&")}"]`) as HTMLImageElement | null;
    if (!img) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = img.offsetWidth;
    const startH = img.offsetHeight;
    const ratio = startW / startH;
    setResizing(true);
    setResizeDimensions({ w: startW, h: startH });

    const onMove = (moveEvent: MouseEvent) => {
      const currentImg = editorRef.current?.querySelector(`img[src="${CSS.escape ? CSS.escape(selectedImageSrc) : selectedImageSrc.replace(/["\\]/g, "\\$&")}"]`) as HTMLImageElement | null;
      if (!currentImg) return;
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      let newW = startW;
      let newH = startH;
      if (corner.includes("e")) newW = startW + dx;
      if (corner.includes("w")) newW = startW - dx;
      if (corner.includes("s")) newH = startH + dy;
      if (corner.includes("n")) newH = startH - dy;
      const scale = Math.max(newW / startW, newH / startH);
      let finalW = Math.max(40, startW * scale);
      let finalH = Math.max(40 / ratio, startH * scale);
      if (finalW < 10) finalW = 10;
      if (finalH < 10) finalH = 10;
      currentImg.style.width = `${finalW}px`;
      currentImg.style.height = `${finalH}px`;
      currentImg.style.maxWidth = "none";
      setResizeDimensions({ w: Math.round(finalW), h: Math.round(finalH) });
      if (overlayRef.current && wrapperRef.current) {
        const imgRect = currentImg.getBoundingClientRect();
        const wrapRect = wrapperRef.current.getBoundingClientRect();
        overlayRef.current.style.top = `${imgRect.top - wrapRect.top + wrapperRef.current.scrollTop}px`;
        overlayRef.current.style.left = `${imgRect.left - wrapRect.left + wrapperRef.current.scrollLeft}px`;
        overlayRef.current.style.width = `${currentImg.offsetWidth}px`;
        overlayRef.current.style.height = `${currentImg.offsetHeight}px`;
      }
    };
    const onUp = () => {
      setResizing(false);
      handleInput();
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div ref={wrapperRef} className="border border-gray-200 rounded-xl overflow-hidden focus-within:border-[#004A99] focus-within:ring-1 focus-within:ring-[#004A99]/30 transition-all relative">
      {label && <div className="px-3 pt-3 pb-1"><p className="text-xs font-semibold text-gray-600">{label}</p></div>}

      {/* Toolbar */}
      <div className="sticky top-0 z-20 bg-[#F8F9FA] border-b border-gray-200 px-2 py-1.5 flex flex-wrap items-center gap-0.5">
        {/* Heading */}
        <select
          className="text-xs border border-gray-200 rounded px-1 py-1 bg-white cursor-pointer outline-none text-gray-700 mr-1"
          onChange={(e) => handleHeading(e.target.value)}
          defaultValue=""
        >
          <option value="" disabled>Kiểu</option>
          <option value="p">Bình thường</option>
          <option value="h1">Tiêu đề 1</option>
          <option value="h2">Tiêu đề 2</option>
          <option value="h3">Tiêu đề 3</option>
          <option value="blockquote">Trích dẫn</option>
        </select>

        {/* Font size */}
        <select
          className="text-xs border border-gray-200 rounded px-1 py-1 bg-white cursor-pointer outline-none text-gray-700 mr-1"
          onChange={(e) => handleFontSize(e.target.value)}
          defaultValue=""
        >
          <option value="" disabled>Cỡ chữ</option>
          <option value="12px">12px</option>
          <option value="14px">14px</option>
          <option value="16px">16px</option>
          <option value="18px">18px</option>
          <option value="20px">20px</option>
          <option value="24px">24px</option>
          <option value="28px">28px</option>
          <option value="32px">32px</option>
          <option value="36px">36px</option>
          <option value="48px">48px</option>
        </select>

        <ToolbarDivider />

        {/* Format */}
        <ToolbarButton title="Đậm (Ctrl+B)" icon="ri-bold" active={isActive("bold")} onClick={() => execCmd("bold")} />
        <ToolbarButton title="Nghiêng (Ctrl+I)" icon="ri-italic" active={isActive("italic")} onClick={() => execCmd("italic")} />
        <ToolbarButton title="Gạch dưới (Ctrl+U)" icon="ri-underline" active={isActive("underline")} onClick={() => execCmd("underline")} />
        <ToolbarButton title="Gạch ngang" icon="ri-strikethrough" active={isActive("strikeThrough")} onClick={() => execCmd("strikeThrough")} />

        <ToolbarDivider />

        {/* Text color */}
        <div className="relative group">
          <button
            type="button"
            title="Màu chữ"
            className="w-7 h-7 flex flex-col items-center justify-center rounded cursor-pointer hover:bg-gray-100 gap-0.5"
          >
            <i className="ri-font-color text-sm text-gray-600" />
            <div className="w-5 h-1 rounded-sm bg-[#004A99]" />
          </button>
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-2 hidden group-hover:flex flex-wrap gap-1 w-36">
            <p className="text-[10px] text-gray-400 w-full mb-1">Màu chữ</p>
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleColor(c); }}
                title={c}
                className={`w-5 h-5 rounded-full border cursor-pointer hover:scale-110 transition-transform ${c === "#ffffff" ? "border-gray-300" : "border-transparent"}`}
                style={{ background: c }}
              />
            ))}
          </div>
        </div>

        {/* BG color */}
        <div className="relative group">
          <button
            type="button"
            title="Nền chữ"
            className="w-7 h-7 flex flex-col items-center justify-center rounded cursor-pointer hover:bg-gray-100 gap-0.5"
          >
            <i className="ri-mark-pen-line text-sm text-gray-600" />
            <div className="w-5 h-1 rounded-sm bg-yellow-300" />
          </button>
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-2 hidden group-hover:flex flex-wrap gap-1 w-36">
            <p className="text-[10px] text-gray-400 w-full mb-1">Màu nền</p>
            {BG_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleBgColor(c); }}
                title={c}
                className={`w-5 h-5 rounded-full border cursor-pointer hover:scale-110 transition-transform ${c === "#fff" || c === "transparent" ? "border-gray-300" : "border-transparent"}`}
                style={{ background: c || "#eee" }}
              />
            ))}
          </div>
        </div>

        <ToolbarDivider />

        {/* Alignment */}
        <ToolbarButton title="Căn trái" icon="ri-align-left" onClick={() => execCmd("justifyLeft")} />
        <ToolbarButton title="Căn giữa" icon="ri-align-center" onClick={() => execCmd("justifyCenter")} />
        <ToolbarButton title="Căn phải" icon="ri-align-right" onClick={() => execCmd("justifyRight")} />

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarButton title="Danh sách không thứ tự" icon="ri-list-unordered" onClick={() => execCmd("insertUnorderedList")} />
        <ToolbarButton title="Danh sách có thứ tự" icon="ri-list-ordered" onClick={() => execCmd("insertOrderedList")} />

        <ToolbarDivider />

        {/* Link + Image */}
        <ToolbarButton title="Chèn liên kết (mở tab mới)" icon="ri-link" onClick={handleLink} />
        <ToolbarButton title="Chèn hình ảnh" icon="ri-image-add-line" onClick={() => { saveRange(); setShowImageModal(true); }} />

        <ToolbarDivider />

        {/* Clean */}
        <ToolbarButton title="Xóa định dạng" icon="ri-eraser-line" onClick={() => execCmd("removeFormat")} />
        <ToolbarButton title="Xóa tất cả" icon="ri-delete-bin-6-line" onClick={() => { if (editorRef.current) { editorRef.current.innerHTML = ""; handleInput(); } }} />

        {/* If image selected — show alignment toolbar */}
        {selectedImageSrc && (
          <>
            <ToolbarDivider />
            <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-lg px-2 py-0.5">
              <span className="text-[10px] text-amber-700 font-semibold mr-1">Căn ảnh:</span>
              {(["left", "center", "right"] as const).map((align) => (
                <button
                  key={align}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); setSelectedImageAlignment(align); }}
                  title={align === "left" ? "Căn trái" : align === "center" ? "Căn giữa" : "Căn phải"}
                  className={`w-6 h-6 flex items-center justify-center rounded cursor-pointer transition-colors ${
                    selectedImageAlign === align ? "bg-amber-500 text-white" : "text-amber-600 hover:bg-amber-100"
                  }`}
                >
                  <i className={align === "left" ? "ri-align-left" : align === "center" ? "ri-align-center" : "ri-align-right"} />
                </button>
              ))}
            </div>
          </>
        )}

        {/* Paste from Google Docs hint */}
        <div className="ml-auto flex items-center gap-1 text-[10px] text-gray-400 px-2">
          <i className="ri-google-line text-xs" />
          <span className="hidden sm:inline">Paste từ Google Docs: tự động làm sạch + nhận diện link</span>
        </div>
      </div>

      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        onClick={handleEditorClick}
        data-placeholder={placeholder}
        className="prose prose-sm max-w-none px-4 py-3 outline-none text-gray-800 leading-relaxed rich-editor-content"
        style={{ minHeight, overflowY: "auto", maxHeight: "600px" }}
      />

      {/* Image resize overlay */}
      {selectedImageSrc && (
        <div
          ref={overlayRef}
          className="absolute pointer-events-none"
          style={{ border: "2px dashed #004A99", zIndex: 100, boxSizing: "border-box" }}
        >
          {/* Dimension badge */}
          {resizeDimensions && (
            <div className="absolute -top-6 left-0 bg-[#004A99] text-white text-[10px] font-mono px-1.5 py-0.5 rounded whitespace-nowrap">
              {resizeDimensions.w} × {resizeDimensions.h}px
            </div>
          )}
          {/* Resize handles — 4 corners */}
          {(["nw", "ne", "se", "sw"] as const).map((corner) => (
            <div
              key={corner}
              onMouseDown={(e) => startResize(e, corner)}
              className="absolute w-3 h-3 bg-white border-2 border-[#004A99] rounded-sm pointer-events-auto cursor-pointer hover:bg-[#004A99] transition-colors"
              style={{
                top: corner.includes("n") ? -5 : undefined,
                bottom: corner.includes("s") ? -5 : undefined,
                left: corner.includes("w") ? -5 : undefined,
                right: corner.includes("e") ? -5 : undefined,
                cursor: resizing ? "nwse-resize" : `${corner}-resize`,
              }}
            />
          ))}
        </div>
      )}

      {/* Resizing dimension tooltip */}
      {resizing && resizeDimensions && (
        <div
          className="fixed z-[300] bg-gray-900 text-white text-xs font-mono px-2 py-1 rounded pointer-events-none"
          style={{ top: 12, right: 12 }}
        >
          {resizeDimensions.w} × {resizeDimensions.h}px
        </div>
      )}

      {/* Image Insert Modal */}
      {showImageModal && (
        <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">Chèn hình ảnh</h3>
              <button onClick={() => setShowImageModal(false)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            <ImageUploader
              images={imageUrls}
              onChange={setImageUrls}
              maxImages={5}
              folder={folder}
              label="Chọn hình ảnh để chèn"
            />

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Kích cỡ (%)</label>
                <select
                  value={imageSize}
                  onChange={(e) => setImageSize(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm cursor-pointer outline-none"
                >
                  <option value="25">25% - Nhỏ</option>
                  <option value="50">50% - Vừa</option>
                  <option value="75">75% - Lớn</option>
                  <option value="100">100% - Toàn trang</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Căn chỉnh</label>
                <select
                  value={imageAlign}
                  onChange={(e) => setImageAlign(e.target.value as "left" | "center" | "right")}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm cursor-pointer outline-none"
                >
                  <option value="left">Trái</option>
                  <option value="center">Giữa</option>
                  <option value="right">Phải</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowImageModal(false)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                Hủy
              </button>
              <button
                onClick={handleInsertImages}
                disabled={!imageUrls.length}
                className="flex-1 py-2.5 bg-[#004A99] text-white rounded-xl text-sm font-semibold cursor-pointer hover:bg-[#003a7a] disabled:opacity-50 whitespace-nowrap"
              >
                Chèn vào bài ({imageUrls.length})
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .rich-editor-content {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 15px;
          line-height: 1.75;
          color: #1a1a1a;
          white-space: pre-wrap;
        }
        .rich-editor-content:empty::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .rich-editor-content h1 { font-size: 2em; font-weight: 700; margin: 0.75em 0 0.4em; line-height: 1.3; }
        .rich-editor-content h2 { font-size: 1.5em; font-weight: 700; margin: 0.65em 0 0.35em; line-height: 1.35; }
        .rich-editor-content h3 { font-size: 1.25em; font-weight: 600; margin: 0.6em 0 0.3em; line-height: 1.4; }
        .rich-editor-content blockquote {
          border-left: 4px solid #004A99;
          padding: 8px 12px;
          color: #555;
          font-style: italic;
          margin: 12px 0;
          background: #f8f9fa;
          border-radius: 0 8px 8px 0;
        }
        .rich-editor-content ul { list-style-type: disc; padding-left: 24px; margin: 8px 0; }
        .rich-editor-content ol { list-style-type: decimal; padding-left: 24px; margin: 8px 0; }
        .rich-editor-content li { margin: 4px 0; }
        .rich-editor-content a {
          color: #0066cc;
          text-decoration: underline;
          cursor: pointer;
          transition: color 0.15s ease;
        }
        .rich-editor-content a:hover { color: #004A99; }
        .rich-editor-content img { max-width: 100%; border-radius: 8px; height: auto; box-sizing: border-box; }
        .rich-editor-content img[style*="width:"] { max-width: 100% !important; height: auto; }
        .rich-editor-content p { margin: 6px 0; min-height: 1.2em; }
        .rich-editor-content b, .rich-editor-content strong { font-weight: 700; }
        .rich-editor-content i, .rich-editor-content em { font-style: italic; }
        .rich-editor-content u { text-decoration: underline; }
        /* Emoji and special characters render natively via UTF-8 */
        .rich-editor-content * { max-width: 100%; box-sizing: border-box; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
